import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle } from 'lucide-react';
import type { Notification } from '@swiss-magic/shared-types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/lib/api/notifications';
import { usePolledData } from '@/lib/usePolledData';
import { safeRedirectOrDefault } from '@/lib/safeRedirect';
import { showImportantNotificationToast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const POLL_DEPS: readonly unknown[] = [];

// Story 010a — module-level Set of important-notification ids the bell
// has surfaced in this session. First sight of an unread important id
// not in the Set fires a persistent toast; subsequent polls don't
// re-fire. Resets on full page reload — fresh tab opens, the badge +
// ordering already make the priority visible; the toast is the
// real-time-since-load signal.
const seenImportantIds = new Set<string>();

function relativeTime(iso: string, t: TFunction): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return t('notifications_bell.time.just_now');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t('notifications_bell.time.minutes_ago', { count: diffMin });
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return t('notifications_bell.time.hours_ago', { count: diffHr });
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return t('notifications_bell.time.days_ago', { count: diffDay });
  return new Date(iso).toLocaleDateString();
}

export function NotificationsBell() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, refetch } = usePolledData<Notification[]>(
    () => listNotifications(),
    POLL_DEPS,
  );
  const notifications = data ?? [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const hasUnreadImportant = notifications.some(
    (n) => !n.isRead && n.priority === 'important',
  );

  // Story 010a — fire a persistent toast on first sight of a newly
  // arrived unread important notification. Tracked across polls via a
  // module-level Set so the bell doesn't re-fire on every refresh.
  //
  // Bail until the first poll resolves (data === null). On the *first*
  // poll, seed the Set with everything currently in the bell so we
  // don't pop toasts for the user's existing backlog — only items
  // arriving in subsequent polls qualify as "new since the page
  // loaded". Otherwise an unread important notification that existed
  // *before* the page loaded would steal focus on every navigation.
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (data === null) return; // not yet loaded
    if (firstRunRef.current) {
      for (const n of data) {
        if (n.priority === 'important') seenImportantIds.add(n.id);
      }
      firstRunRef.current = false;
      return;
    }
    for (const n of data) {
      if (
        n.priority === 'important' &&
        !n.isRead &&
        !seenImportantIds.has(n.id)
      ) {
        seenImportantIds.add(n.id);
        showImportantNotificationToast(n.title);
      }
    }
  }, [data]);

  async function handleSelect(n: Notification) {
    // Optimistic mark-read so the badge updates immediately; refetch
    // afterward so a server-side discrepancy heals on its own.
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
      } catch {
        // Swallow — the next poll will reconcile state.
      }
    }
    refetch();
    // FE-L3: validate even server-sourced paths at the boundary.
    if (n.actionUrl) navigate(safeRedirectOrDefault(n.actionUrl, '/'));
  }

  async function handleMarkAllRead() {
    try {
      await markAllNotificationsRead();
    } catch {
      // Swallow — next poll reconciles.
    }
    refetch();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notifications-bell"
          aria-label={t('notifications_bell.aria_label')}
          className="relative h-9 w-9"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              data-testid="notifications-unread-badge"
              data-priority={hasUnreadImportant ? 'important' : 'normal'}
              className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-medium leading-none text-white ${
                hasUnreadImportant ? 'animate-pulse' : ''
              }`}
            >
              {unreadCount}
            </span>
          )}
          {/* Story 010a — when there's an unread important notification,
              also overlay a red exclamation-mark badge at the top-left of
              the bell so the priority is visible at a glance even
              without reading the count. The right-side count badge
              stays as the unread total + animate-pulse. */}
          {hasUnreadImportant && (
            <span
              data-testid="notifications-important-indicator"
              aria-label={t('notifications_bell.important_indicator_aria')}
              className="pointer-events-none absolute -left-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold leading-none text-white animate-pulse"
            >
              !
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="notifications-panel"
        className="w-80 max-h-[70vh] overflow-y-auto p-0"
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">{t('notifications_bell.panel_title')}</span>
          {unreadCount > 0 && (
            <button
              type="button"
              data-testid="notifications-mark-all-read"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={handleMarkAllRead}
            >
              {t('notifications_bell.mark_all_read')}
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <p
            data-testid="notifications-empty-state"
            className="px-3 py-6 text-center text-sm text-muted-foreground"
          >
            {t('notifications_bell.empty')}
          </p>
        ) : (
          <ul className="divide-y">
            {notifications.map((n) => {
              const isImportant = n.priority === 'important';
              // Story 010a — important rows get a red left-border (instead
              // of primary) and an AlertTriangle icon, kept on read+unread
              // so the priority is always visible.
              const borderClass = isImportant
                ? 'border-l-2 border-l-red-600'
                : !n.isRead
                  ? 'border-l-2 border-l-primary'
                  : '';
              const opacityClass = n.isRead ? 'opacity-70' : '';
              // Story 010b iteration 2 — Confirm/Dispute lives inline on
              // the My Matches card now (consistent with self-report).
              // The notification just deep-links there via actionUrl.
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    data-testid={`notification-item-${n.id}`}
                    data-priority={n.priority}
                    onClick={() => handleSelect(n)}
                    className={`flex w-full flex-col gap-1 px-3 py-2 text-left hover:bg-muted/50 ${borderClass} ${opacityClass}`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      {isImportant && (
                        <AlertTriangle
                          className="h-3.5 w-3.5 shrink-0 text-red-600"
                          aria-label={t('notifications_bell.important_icon_aria')}
                          data-testid={`notification-important-icon-${n.id}`}
                        />
                      )}
                      {n.title}
                    </span>
                    {n.body && (
                      <span className="text-xs text-muted-foreground">{n.body}</span>
                    )}
                    <span className="text-[10px] text-muted-foreground">
                      {relativeTime(n.createdAt, t)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

